export const getTreeNode = (path_, root) => {
  // Returns the node corresponding to the path, null if path does not exist or state.options if path empty
  const rootNode = root;
  const path = [...path_];
  if (!path || path.length === 0) {
    return rootNode;
  }
  let node = rootNode.children[path[0]];
  const remainingPath = path.slice(1);
  for (const index of remainingPath) {
    if ('children' in node && node.children.length > 0) {
      node = node.children[index];
    } else {
      return null;
    }
  }
  return node;
};

export const getTreePath = (tree, node, equals) => {
  if (equals(node, tree)) {
    return [];
  }
  if (tree.children) {
    for (let i = 0; i < tree.children.length; i += 1) {
      const path = getTreePath(tree.children[i], node, equals);
      if (path !== null) {
        path.unshift(i);
        return path;
      }
    }
  }
  return null;
};

const getFirstLeafPath = (node) => {
  if (!node.children || !node.children.length) {
    return [];
  }
  return [0, ...getFirstLeafPath(node.children[0])];
};

const getLastLeafPath = (node) => {
  if (!node.children || !node.children.length) {
    return [];
  }
  return [
    node.children.length - 1,
    ...getLastLeafPath(node.children[node.children.length - 1]),
  ];
};

export const getNextPath = (path, tree, flattened = false) => {
  let node;
  let nextPath = [...path];
  path.forEach(() => {
    const index = Number(nextPath.splice(-1));
    nextPath.push(index + 1);
    node = getTreeNode(nextPath, tree);
    if (node) {
      if (flattened) {
        // Tree is flattened, get first leaf of the node.
        nextPath = [...nextPath, ...getFirstLeafPath(node)];
      }
    } else {
      nextPath.splice(-1);
    }
  });
  return nextPath;
};

export const getPreviousPath = (path, tree, flattened = false) => {
  const previousPath = path;
  let index = Number(previousPath.splice(-1));
  if (!flattened) {
    // Previous node in terms of navigation is the direct parent in this case
    if (index === 0 && previousPath.length) {
      return previousPath;
    }
    // no parent => first node of the tree => we need to go to the last element of the tree
    if (index === 0) {
      return getPreviousPath([tree.children.length], tree);
    }
    previousPath.push(index - 1);
    let node = getTreeNode(previousPath, tree);
    while (node.children && node.children.length > 0 && node.isOpen) {
      previousPath.push(node.children.length - 1);
      node = getTreeNode(previousPath, tree);
    }
    return previousPath;
  }

  while (index === 0 && previousPath.length) {
    index = Number(previousPath.splice(-1));
  }
  if (index === 0) {
    // Return the last leaf node of the tree.
    return getPreviousPath([tree.children.length], tree, true);
  }
  const newNode = getTreeNode([...previousPath, index - 1], tree);
  return [...previousPath, index - 1, ...getLastLeafPath(newNode)];
};

export const openAlongPath = (tree, path_) => {
  const path = [...path_];
  if (!path || path.length === 0) {
    return tree;
  }
  const index = path.shift();
  if (!tree.children || tree.children.length <= index) {
    return null;
  }
  const node = { ...tree };
  node.isOpen = true;
  node.children[index] = openAlongPath(node.children[index], path);
  return node;
};

export const onArrowDown = (tree, focusPath, flattened = false) => {
  if (!focusPath || focusPath.length === 0) {
    return { focusPath: flattened ? getFirstLeafPath(tree) : [0] };
  }
  // Get item index of the path
  const path = [...focusPath];
  const index = Number(path.splice(-1)[0]);
  const parent = !path || path.length === 0 ? tree : getTreeNode(path, tree);
  if (parent === null || !parent.children || parent.children.length <= index) {
    return null;
  }
  const node = parent.children[index];
  if (node.children && node.children.length > 0 && node.isOpen) {
    // Select the first children of the node.
    path.push(index);
    path.push(0);
    return { focusPath: path };
  } else if (path.length > 0 && index === parent.children.length - 1) {
    // node is closed, it is the last of his parent's children and is not at the root of the tree
    // => go to next node with inline traversal
    const nextPath = getNextPath(path, tree, flattened);
    return { focusPath: nextPath };
  }
  // Select the next node or the first node in the tree.
  path.push(index < parent.children.length - 1 ? index + 1 : 0);
  if (!flattened) {
    return { focusPath: path };
  }
  // If flattened, select the first leaf of that node.
  const newNode = getTreeNode(path, tree);
  return { focusPath: [...path, ...getFirstLeafPath(newNode)] };
};

export const onArrowUp = (tree, focusPath, flattened = false) => {
  if (!focusPath || focusPath.length === 0) {
    return {
      focusPath: getPreviousPath([tree.children.length], tree, flattened),
    };
  }
  const path = [...focusPath];
  return { focusPath: getPreviousPath(path, tree, flattened) };
};

export const onArrowRight = (options, focusPath) => {
  if (!focusPath || focusPath.length === 0) {
    return null;
  }
  const path = [...focusPath];
  const options_ = [...options];

  if (path.length === 0) {
    return null;
  }

  const index = Number(path.splice(-1)[0]);
  const rootNode = { children: options_ };
  const parent =
    !path || path.length === 0 ? rootNode : getTreeNode(path, rootNode);
  if (parent === null || !parent.children || parent.children.length <= index) {
    return null;
  }
  let node = parent.children[index];
  if (
    !node.children ||
    node.children.length === 0 ||
    node.isExpandable === false
  ) {
    return null;
  }
  if (!node.isOpen) {
    // open children list
    node = { ...node };
    node.isOpen = true;
    parent.children[index] = node;
    return { options: options_ };
  }
  // focus on first of the children
  path.push(index);
  path.push(0);
  return { focusPath: path };
};

export const onArrowLeft = (options, focusPath) => {
  if (!focusPath || focusPath.length === 0) {
    return null;
  }
  const path = [...focusPath];
  const options_ = [...options];

  if (path.length === 0) {
    return null;
  }

  const index = Number(path.splice(-1)[0]);
  const rootNode = { children: options_ };
  const parent =
    !path || path.length === 0 ? rootNode : getTreeNode(path, rootNode);
  if (parent === null || !parent.children || parent.children.length <= index) {
    return null;
  }
  let node = parent.children[index];
  if (node.isOpen) {
    // open children list
    node = { ...node };
    node.isOpen = false;
    parent.children[index] = node;
    return { options: options_ };
  }
  // path here represents the path of the parent
  if (path.length > 0) {
    return { focusPath: path };
  }

  return null;
};
